import { useCallback, useEffect, useRef, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { useReducedMotion } from "framer-motion";
import { ChevronLeft, ChevronRight, ExternalLink, PenLine, Quote } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { Stars } from "@/components/Stars";
import { useContact } from "@/lib/contact";
import { PUBLIC_FALLBACK_CONTENT, useContent } from "@/context/ContentContext";
import { api } from "@/lib/api";

const AUTOPLAY_MS = 7000; // إيقاع هادئ
const RESUME_DELAY_MS = 4000; // استئناف بعد انتهاء التفاعل

const GoogleG = ({ className = "h-8 w-8" }) => (
  <svg viewBox="0 0 48 48" className={className} aria-hidden="true">
    <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303C33.654 32.657 29.223 36 24 36c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" />
    <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" />
    <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
    <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571l6.19 5.238C41.38 34.463 44 29.657 44 24c0-1.341-.138-2.65-.389-3.917z" />
  </svg>
);

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("ar-BH", { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return "";
  }
};

export const ReviewsSection = () => {
  const CONTACT = useContact();
  const { content } = useContent();
  const fallback = content || PUBLIC_FALLBACK_CONTENT;
  const [data, setData] = useState(null);
  const reduceMotion = useReducedMotion();

  const [emblaRef, emblaApi] = useEmblaCarousel({ direction: "rtl", loop: true, align: "start", duration: 32 });
  const pausedRef = useRef(false);
  const resumeTimer = useRef(null);

  useEffect(() => {
    let alive = true;
    api
      .get("/reviews")
      .then(({ data: d }) => alive && setData(d))
      .catch(() => alive && setData(null));
    return () => {
      alive = false;
    };
  }, []);

  const pause = useCallback(() => {
    pausedRef.current = true;
    clearTimeout(resumeTimer.current);
  }, []);
  const resume = useCallback(() => {
    clearTimeout(resumeTimer.current);
    resumeTimer.current = setTimeout(() => {
      pausedRef.current = false;
    }, RESUME_DELAY_MS);
  }, []);

  // تقدّم تلقائي هادئ: يتوقف أثناء القراءة/التفاعل ويُحترم إعداد تقليل الحركة
  useEffect(() => {
    if (!emblaApi || reduceMotion) return undefined;
    const id = setInterval(() => {
      if (!pausedRef.current && !document.hidden) emblaApi.scrollNext();
    }, AUTOPLAY_MS);
    return () => clearInterval(id);
  }, [emblaApi, reduceMotion]);

  const rating = data?.rating ?? fallback.rating;
  const reviewsCount = data?.reviewsCount ?? fallback.reviewsCount;
  const reviews = data?.reviews?.length ? data.reviews : fallback.reviews;
  const synced = data?.source === "google";
  const reviewUrl = content?.contact?.reviewUrl || CONTACT.mapsUrl;

  return (
    <section id="reviews" className="relative py-24 sm:py-32 bg-[#F8F9FA]">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHeading caption="آراء العملاء" title="تقييم Google وآراء العملاء" />

        <Reveal>
          <div
            data-testid="google-rating-badge"
            className="glass brand-ring rounded-3xl p-8 sm:p-10 flex flex-col sm:flex-row items-center gap-8 sm:gap-12"
          >
            <div className="flex items-center gap-5">
              <GoogleG className="h-12 w-12" />
              <div>
                <div className="flex items-end gap-3">
                  <span data-testid="google-rating-score" className="font-display text-6xl font-bold text-brand leading-none" dir="ltr">
                    {rating}
                  </span>
                  <span className="text-slate-600 text-lg mb-1">/ 5</span>
                </div>
                <Stars value={rating} size="h-5 w-5" />
              </div>
            </div>
            <div className="flex-1 text-center sm:text-start">
              <p className="text-base sm:text-lg text-slate-900 leading-relaxed">
                <span dir="ltr">{rating}</span> من 5 على Google — بناءً على{" "}
                <span data-testid="google-reviews-count" dir="ltr" className="text-brand font-bold">
                  {reviewsCount}
                </span>{" "}
                تقييمًا
              </p>
              <p className="mt-2 text-xs text-slate-500" data-testid="reviews-selection-note">
                التقييم العام وعدد التقييمات يشملان جميع تقييمات المكتب — والمعروض أدناه آراء مختارة بتقييم 5 نجوم
              </p>
            </div>
            <div className="flex flex-col gap-3">
              <a
                href={CONTACT.mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="google-reviews-link"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-[#C80000]/45 px-6 py-3 text-sm font-bold text-brand hover:bg-[#C80000] hover:text-white transition-colors duration-300"
              >
                شاهد جميع التقييمات على Google
                <ExternalLink className="h-4 w-4" />
              </a>
              <a
                href={reviewUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="google-write-review-link"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#C80000] px-6 py-3 text-sm font-bold text-white hover:bg-[#E01010] transition-colors duration-300"
              >
                اكتب تقييمك
                <PenLine className="h-4 w-4" />
              </a>
            </div>
          </div>
        </Reveal>

        <div className="relative mt-10" data-testid="reviews-carousel">
          <div
            ref={emblaRef}
            className="overflow-hidden"
            dir="rtl"
            onPointerEnter={pause}
            onPointerLeave={resume}
            onPointerDown={pause}
            onFocusin={pause}
            onFocusout={resume}
          >
            <div className="flex">
              {reviews.map((review, i) => (
                <div key={review.id || i} className="min-w-0 flex-[0_0_100%] md:flex-[0_0_50%] px-3">
                  <article
                    data-testid={`customer-review-card-${i + 1}`}
                    className="group relative h-full rounded-3xl border border-slate-200 bg-[#FFFFFF] p-7 transition-all duration-500 hover:-translate-y-2 hover:border-[#C80000]/40 hover:shadow-[0_24px_60px_-24px_rgba(200,0,0,0.45)]"
                  >
                    <Quote className="h-7 w-7 text-[#C80000]/45 mb-4" />
                    <p dir="auto" className="text-sm leading-relaxed text-slate-600 text-left min-h-[96px]">
                      {review.text}
                    </p>
                    <div className="mt-6 pt-5 border-t border-slate-200 flex items-center justify-between gap-3">
                      <div>
                        <p dir="auto" className="text-sm font-bold text-[#1A1A1E] text-left">
                          {review.name}
                        </p>
                        <p className="text-[11px] text-slate-500 mt-0.5">
                          مراجعة على Google{review.date ? ` — ${formatDate(review.date)}` : ""}
                        </p>
                      </div>
                      <Stars value={review.stars} size="h-3.5 w-3.5" />
                    </div>
                  </article>
                </div>
              ))}
            </div>
          </div>

          {reviews.length > 1 && (
            <div className="mt-8 flex items-center justify-center gap-4">
              <button
                type="button"
                onClick={() => emblaApi?.scrollPrev()}
                aria-label="التقييم السابق"
                data-testid="reviews-prev"
                className="h-11 w-11 rounded-full border border-slate-200 bg-white flex items-center justify-center text-slate-600 hover:border-[#C80000]/60 hover:text-brand transition-colors"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
              <span className="text-[11px] text-slate-400 font-semibold" data-testid="reviews-source-note">
                {synced ? "محدّثة يوميًا من ملف المكتب على Google" : "آراء حقيقية من ملف المكتب على Google"}
              </span>
              <button
                type="button"
                onClick={() => emblaApi?.scrollNext()}
                aria-label="التقييم التالي"
                data-testid="reviews-next"
                className="h-11 w-11 rounded-full border border-slate-200 bg-white flex items-center justify-center text-slate-600 hover:border-[#C80000]/60 hover:text-brand transition-colors"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
